import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { userService } from '../services/userService';
import hotelService from '../services/hotelService';
import permissionService from '../services/permissionService';
import invitationService from '../services/invitationService';
import partnerService from '../services/partnerService';
import './UserForm.css';

const UserForm = ({ mode = 'create' }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    useruuid: '',
    SUB: '',
    ACCOUNTID: '',
    username: '',
    email: '',
    firstname: '',
    lastname: '',
    USERROLE: '',
    loginname: '',
    defaultaccountid: '',
    inactivedate: ''
  });

  const [permissions, setPermissions] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [partners, setPartners] = useState([]);
  const [permSaving, setPermSaving] = useState(false);
  const [permMessage, setPermMessage] = useState(null); // { type: 'success'|'error', text }
  const [addingPerm, setAddingPerm] = useState(false);
  const [newPerm, setNewPerm] = useState({ type: 'hotel', resourceId: '', permissionType: 'U' });

  const isEditMode = mode === 'edit';
  const isViewMode = mode === 'view';

  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      loadUser();
    }
  }, [id, isEditMode, isViewMode]);

  const loadUser = async () => {
    try {
      setLoading(true);
      const user = await userService.getUserById(id);
      setFormData({
        useruuid: user.useruuid || '',
        SUB: user.SUB || '',
        ACCOUNTID: user.ACCOUNTID || '',
        username: user.username || '',
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        USERROLE: user.USERROLE || '',
        loginname: user.loginname || '',
        defaultaccountid: user.defaultaccountid || '',
        inactivedate: user.inactivedate ? user.inactivedate.split('T')[0] : ''
      });
      await Promise.all([
        loadPermissions(),
        loadInvitations(),
        loadHotels(user.ACCOUNTID),
        loadPartners()
      ]);
    } catch (err) {
      console.error('Error loading user:', err);
      setError(err.response?.data?.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await permissionService.getPermissionsByUserId(id);
      setPermissions(response.permissions || []);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setPermMessage({ type: 'error', text: 'Failed to load permissions' });
    }
  };

  const loadInvitations = async () => {
    try {
      const response = await invitationService.getInvitationsByUserId(id);
      setInvitations(response.invitations || []);
    } catch (err) {
      console.error('Error loading invitations:', err);
    }
  };

  const loadHotels = async (accountId) => {
    if (!accountId) return;
    try {
      const response = await hotelService.getAllHotels({ accountid: accountId });
      setHotels(response.hotels || []);
    } catch (err) {
      console.error('Error loading hotels:', err);
    }
  };

  const loadPartners = async () => {
    try {
      const response = await partnerService.getAllPartners();
      setPartners(response.partners || response.data?.partners || response.data || []);
    } catch (err) {
      console.error('Error loading partners:', err);
    }
  };

  const handlePermTypeChange = async (permId, newType) => {
    setPermSaving(true);
    setPermMessage(null);
    try {
      const perm = permissions.find(p => p.id === permId);
      await permissionService.updatePermission(permId, {
        userid: perm.userid,
        hotel_chain_id: perm.hotel_chain_id || null,
        hotelid: perm.hotelid || null,
        permission_type: newType
      });
      setPermissions(prev => prev.map(p => p.id === permId ? { ...p, permission_type: newType } : p));
    } catch (err) {
      setPermMessage({ type: 'error', text: 'Failed to update permission' });
    } finally {
      setPermSaving(false);
    }
  };

  const handlePermDelete = async (permId) => {
    setPermSaving(true);
    setPermMessage(null);
    try {
      await permissionService.deletePermission(permId);
      setPermissions(prev => prev.filter(p => p.id !== permId));
    } catch (err) {
      setPermMessage({ type: 'error', text: 'Failed to remove permission' });
    } finally {
      setPermSaving(false);
    }
  };

  const handleCancelInvitation = async (invId) => {
    setPermSaving(true);
    setPermMessage(null);
    try {
      await invitationService.cancelInvitation(invId);
      setInvitations(prev => prev.filter(i => i.id !== invId));
    } catch (err) {
      setPermMessage({ type: 'error', text: err.response?.data?.message || 'Failed to cancel invitation' });
    } finally {
      setPermSaving(false);
    }
  };

  const handleResendInvitation = async (invId) => {
    setPermSaving(true);
    setPermMessage(null);
    try {
      await invitationService.resendInvitation(invId);
      setPermMessage({ type: 'success', text: 'Invitation resent successfully' });
    } catch (err) {
      setPermMessage({ type: 'error', text: err.response?.data?.message || 'Failed to resend invitation' });
    } finally {
      setPermSaving(false);
    }
  };

  const handleAddPerm = async () => {
    if (!newPerm.resourceId) {
      setPermMessage({ type: 'error', text: 'Please select a hotel or chain' });
      return;
    }
    setPermSaving(true);
    setPermMessage(null);
    try {
      await permissionService.createPermission({
        userid: parseInt(id),
        permission_type: newPerm.permissionType,
        hotel_chain_id: newPerm.type === 'chain' ? parseInt(newPerm.resourceId) : null,
        hotelid: newPerm.type === 'hotel' ? parseInt(newPerm.resourceId) : null
      });
      await loadPermissions();
      setAddingPerm(false);
      setNewPerm({ type: 'hotel', resourceId: '', permissionType: 'U' });
      setPermMessage({ type: 'success', text: 'Permission added' });
    } catch (err) {
      setPermMessage({ type: 'error', text: err.response?.data?.message || 'Failed to add permission' });
    } finally {
      setPermSaving(false);
    }
  };

  const getPermLabel = (perm) => {
    if (perm.hotel_chain_id) return `Chain #${perm.hotel_chain_id} (All Hotels)`;
    if (perm.hotelid) {
      const hotel = hotels.find(h => h.hotelid === perm.hotelid);
      return hotel ? `${hotel.hotelname} (${hotel.hotelcode})` : `Hotel #${perm.hotelid}`;
    }
    if (perm.partnerid) {
      const partner = partners.find(p => Number(p.partnerid) === Number(perm.partnerid));
      return partner ? (partner.companyname || `Partner #${perm.partnerid}`) : `Partner #${perm.partnerid}`;
    }
    return 'Unknown';
  };

  // Scope keys of the existing (active) permissions — used to hide accepted invitations that
  // already resulted in a permission, so the same grant isn't shown twice.
  const permScopeKeys = new Set(
    permissions.map(p => {
      if (p.partnerid) return `partner:${p.partnerid}`;
      if (p.hotel_chain_id) return `chain:${p.hotel_chain_id}`;
      if (p.hotelid) return `hotel:${p.hotelid}`;
      return null;
    }).filter(Boolean)
  );
  const visibleInvitations = invitations.filter(
    inv => !(inv.status === 'accepted' && permScopeKeys.has(`${inv.type}:${inv.resource_id}`))
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.firstname || !formData.lastname) {
      setError('First name and last name are required');
      return;
    }

    if (!isEditMode && !isViewMode) {
      if (!formData.email) {
        setError('Email is required for new users');
        return;
      }
    }

    try {
      setLoading(true);

      if (isEditMode) {
        await userService.updateUser(id, formData);
        navigate('/users');
      } else {
        const result = await userService.createUser(formData);
        if (result?.alreadyExisted) {
          setError('A user with this email already exists in the system.');
          setLoading(false);
          return;
        }
        navigate('/users');
      }
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.message || 'Failed to save user');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  if (loading && (isEditMode || isViewMode)) {
    return <div className="loading">Loading user...</div>;
  }

  return (
    <div className="user-form-container">
      <div className="form-header">
        <h1>
          {isViewMode ? 'View User' : isEditMode ? 'Edit User' : 'Create New User'}
        </h1>
      </div>

      {error && <div className="error">{error}</div>}

      <form onSubmit={handleSubmit} className="user-form">
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="firstname">
              First Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="firstname"
              name="firstname"
              value={formData.firstname}
              onChange={handleChange}
              disabled={isViewMode}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastname">
              Last Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="lastname"
              name="lastname"
              value={formData.lastname}
              onChange={handleChange}
              disabled={isViewMode}
              required
            />
          </div>

          {!isEditMode && !isViewMode && (
            <>
              <div className="form-group">
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="user@example.com"
                  required
                />
                <small>This will be used to create the Cognito user account</small>
              </div>

              <div className="form-group">
                <small style={{ color: '#6b7280', display: 'block', marginTop: '-0.5rem' }}>
                  A temporary password will be emailed to the user by Cognito.
                </small>
              </div>
            </>
          )}

          {(isEditMode || isViewMode) && (
            <div className="form-group">
              <label htmlFor="loginname">Login Name / Email</label>
              <input
                type="text"
                id="loginname"
                name="loginname"
                value={formData.loginname}
                onChange={handleChange}
                disabled={isViewMode || isEditMode}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="useruuid">User UUID</label>
            <input
              type="text"
              id="useruuid"
              name="useruuid"
              value={formData.useruuid}
              onChange={handleChange}
              disabled={isViewMode || isEditMode}
              placeholder="Auto-generated if empty"
            />
          </div>

          {(isEditMode || isViewMode) && (
            <div className="form-group">
              <label htmlFor="SUB">Cognito SUB</label>
              <input
                type="text"
                id="SUB"
                name="SUB"
                value={formData.SUB}
                onChange={handleChange}
                disabled={true}
              />
              <small>Generated by Cognito (read-only)</small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="ACCOUNTID">Account ID</label>
            <input
              type="number"
              id="ACCOUNTID"
              name="ACCOUNTID"
              value={formData.ACCOUNTID}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="defaultaccountid">Default Account ID</label>
            <input
              type="number"
              id="defaultaccountid"
              name="defaultaccountid"
              value={formData.defaultaccountid}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="USERROLE">User Role</label>
            <input
              type="number"
              id="USERROLE"
              name="USERROLE"
              value={formData.USERROLE}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="username">Username (Blob)</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          {isEditMode && (
            <div className="form-group">
              <label htmlFor="inactivedate">Inactive Date</label>
              <input
                type="date"
                id="inactivedate"
                name="inactivedate"
                value={formData.inactivedate}
                onChange={handleChange}
                disabled={isViewMode}
              />
              <small>Leave empty to keep user active</small>
            </div>
          )}
        </div>

        {(isEditMode || isViewMode) && (
          <div className="permissions-section">
            <div className="permissions-header">
              <h3>Permissions</h3>
              {isEditMode && (
                <button type="button" className="btn btn-secondary btn-small" onClick={() => { setAddingPerm(true); setPermMessage(null); }} disabled={addingPerm}>
                  + Add
                </button>
              )}
            </div>

            {permMessage && (
              <div className={permMessage.type === 'success' ? 'perm-success-msg' : 'error'} style={{ marginBottom: '0.75rem' }}>
                {permMessage.text}
              </div>
            )}

            {addingPerm && (
              <div className="perm-add-inline">
                <select className="perm-inline-select" value={newPerm.type} onChange={(e) => setNewPerm(p => ({ ...p, type: e.target.value, resourceId: '' }))}>
                  <option value="hotel">Hotel</option>
                  <option value="chain">Chain (All Hotels)</option>
                </select>
                {newPerm.type === 'hotel' ? (
                  <select className="perm-inline-select" value={newPerm.resourceId} onChange={(e) => setNewPerm(p => ({ ...p, resourceId: e.target.value }))}>
                    <option value="">— Select hotel —</option>
                    {hotels.map(h => <option key={h.hotelid} value={h.hotelid}>{h.hotelname} ({h.hotelcode})</option>)}
                  </select>
                ) : (
                  <select className="perm-inline-select" value={newPerm.resourceId} onChange={(e) => setNewPerm(p => ({ ...p, resourceId: e.target.value }))}>
                    <option value="">— Select chain —</option>
                    <option value={formData.ACCOUNTID}>Account #{formData.ACCOUNTID}</option>
                  </select>
                )}
                <select className="perm-inline-select" value={newPerm.permissionType} onChange={(e) => setNewPerm(p => ({ ...p, permissionType: e.target.value }))}>
                  <option value="U">User</option>
                  <option value="A">Admin</option>
                </select>
                <button type="button" className="btn btn-primary btn-small" onClick={handleAddPerm} disabled={permSaving}>Save</button>
                <button type="button" className="btn btn-secondary btn-small" onClick={() => { setAddingPerm(false); setNewPerm({ type: 'hotel', resourceId: '', permissionType: 'U' }); }}>Cancel</button>
              </div>
            )}

            {permissions.length === 0 && visibleInvitations.length === 0 ? (
              <p className="no-permissions">No permissions assigned.</p>
            ) : (
              <table className="permissions-table">
                <thead>
                  <tr>
                    <th>Scope</th>
                    <th>Level</th>
                    <th>Status</th>
                    {isEditMode && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {permissions.map(perm => (
                    <tr key={`perm-${perm.id}`}>
                      <td>{getPermLabel(perm)}</td>
                      <td>
                        {isEditMode ? (
                          <select
                            className="perm-inline-select perm-inline-select-sm"
                            value={perm.permission_type}
                            onChange={(e) => handlePermTypeChange(perm.id, e.target.value)}
                            disabled={permSaving}
                          >
                            <option value="U">User</option>
                            <option value="A">Admin</option>
                          </select>
                        ) : (
                          perm.permission_type === 'A' ? 'Admin' : 'User'
                        )}
                      </td>
                      <td><span className="perm-status perm-status-active">Active</span></td>
                      {isEditMode && (
                        <td>
                          <button type="button" className="btn btn-danger btn-small" onClick={() => handlePermDelete(perm.id)} disabled={permSaving}>Remove</button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {visibleInvitations.map(inv => (
                    <tr key={`inv-${inv.id}`}>
                      <td>{inv.resource_name || `${inv.type} #${inv.resource_id}`}</td>
                      <td>{inv.permission_type === 'A' ? 'Admin' : 'User'}</td>
                      <td>
                        <span className={`perm-status perm-status-${inv.status}`}>
                          {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                        </span>
                      </td>
                      {isEditMode && (
                        <td style={{ display: 'flex', gap: '0.4rem' }}>
                          {inv.status === 'pending' && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-small"
                              onClick={() => handleResendInvitation(inv.id)}
                              disabled={permSaving}
                            >
                              Resend
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-danger btn-small"
                            onClick={() => handleCancelInvitation(inv.id)}
                            disabled={permSaving}
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
          >
            {isViewMode ? 'Back' : 'Cancel'}
          </button>
          {!isViewMode && (
            <>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : isEditMode ? 'Update User' : 'Create User'}
              </button>
            </>
          )}
          {isViewMode && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate(`/users/${id}/edit`)}
            >
              Edit User
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default UserForm;
